import {
    Divider,
    Paragraph,
    Text,
    Flex,
    spaceMixin,
    token,
    Spacer,
    Spinner,
    TooltipPopper,
    Tooltip,
} from '@zillow/constellation';
import PropTypes from 'prop-types';
import { memo } from 'react';
import styled, { css } from 'styled-components';
import { dollar } from '../../../formatters';
import { trackGAMortgageEvent } from '../analytics';
import calculatorImageWebp from '../assets/calculator.webp';
import PrimaryCta from './PrimaryCta';

const BREAKDOWNS = [
    {
        title: 'Principal + interest',
        id: 'monthlyPI',
    },
    {
        title: 'Property taxes',
        id: 'monthlyPropertyTax',
    },
    {
        title: "Homeowner's insurance",
        id: 'monthlyHomeownersInsurance',
    },
    {
        title: 'Private mortgage insurance',
        id: 'monthlyPMI',
    },
];

const commonWrapperCss = css`
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: ${spaceMixin('md')} 0;
    border: 1px solid ${token('colors.borderLight')};
    border-radius: ${token('radii.md')};
`;

const Wrapper = styled.output`
    ${commonWrapperCss}
`;

const DefaultWrapper = styled.output`
    ${commonWrapperCss}
    align-items: center;
    justify-content: center;
`;

const StyledParagraph = styled(Paragraph)`
    text-align: center;
`;

const CalculatorImage = styled.div`
    background-image: url("${calculatorImageWebp}");
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    height: 212px;
    width: 280px;
`;

const BreakdownRow = styled(Flex)`
    ${({ isEven }) => css`
        background-color: ${isEven ? token('colors.backgroundLight') : 'none'};
    `}
`;

const StyledTooltipPopper = styled(TooltipPopper)`
    background-color: ${token('colors.brand')};
`;

const PaymentTooltip = (
    <StyledTooltipPopper
        onOpen={() => {
            trackGAMortgageEvent('Budget Calculator: Interaction', 'Tooltip');
        }}
        triggered={
            <Tooltip
                body={
                    <>
                        <Paragraph>
                            All calculations are estimates and provided for informational purposes only. Actual amounts
                            may vary.
                        </Paragraph>
                        <Paragraph marginTop="xs">
                            Mortgage interest rates are dependent on a number of factors, including credit score, down
                            payment, and repayment length. Certain mortgage lenders with which we have lead or other
                            similar arrangements have provided interest rates as of {new Date().toLocaleDateString()};
                            the Estimated Payment is an average of those rates.
                        </Paragraph>
                    </>
                }
            />
        }
    />
);

const Outputs = ({ outputs, isCalculating }) => {
    if (isCalculating) {
        return (
            <DefaultWrapper>
                <Spinner size="lg" />
                <StyledParagraph fontType="bodySmall" marginTop="md" fontColor="textMedium">
                    One moment while we calculate your results...
                </StyledParagraph>
            </DefaultWrapper>
        );
    }
    if (outputs) {
        return (
            <Wrapper>
                <Flex display="flex" flexDirection="column" flex="1">
                    <Spacer marginX="md">
                        <Paragraph fontType="bodySmallHeading">
                            You can afford a home between <br />{' '}
                            <Text fontType="h4">
                                {dollar(outputs?.lowerBoundHomePrice)} - {dollar(outputs?.upperBoundHomePrice)}
                            </Text>
                        </Paragraph>
                        <Divider marginY="sm" />
                        <Paragraph fontType="bodySmallHeading">
                            Average monthly payment <br /> <Text fontType="h4">{dollar(outputs?.monthlyPayment)}</Text>
                        </Paragraph>
                    </Spacer>

                    <Spacer marginTop="lg">
                        <Paragraph fontType="bodySmallHeading" marginBottom="sm" paddingX="md">
                            Payment breakdown {PaymentTooltip}
                        </Paragraph>

                        {BREAKDOWNS.map(({ title, id }, index) => (
                            <BreakdownRow
                                key={id}
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                                isEven={index % 2 === 0}
                                paddingX="md"
                                paddingY="xs">
                                <Text fontType="bodySmall">{title}</Text>
                                <Text fontType="bodySmallHeading">{dollar(outputs[id])}</Text>
                            </BreakdownRow>
                        ))}
                    </Spacer>
                </Flex>
                <Spacer marginTop="md">
                    <PrimaryCta outputs={outputs} />
                </Spacer>
            </Wrapper>
        );
    }

    return (
        <DefaultWrapper>
            <CalculatorImage />
            <StyledParagraph fontType="bodySmall" fontColor="textMedium" marginX="md" marginTop="md">
                Enter your information to calculate an estimated budget.
            </StyledParagraph>
        </DefaultWrapper>
    );
};

Outputs.propTypes = {
    outputs: PropTypes.object,
    isCalculating: PropTypes.bool,
};

Outputs.defaultProps = {
    isCalculating: false,
};

export default memo(Outputs);
